/**
 * useLogoTinting
 * ---
 * Extracted from `ExcerptImageTab.tsx`. Loads the corner-mark logo (uploaded
 * file at `logoPath` from the `logos` storage bucket, or the bundled default
 * `/excerptus-logo.png`) and runs it through a 4× upscale + `source-in` tint
 * canvas pipeline so the mark color tracks `textColor`. Returns a data-URL
 * (or null while pending / on failure).
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useLogoTinting = (logoPath: string | null, textColor: string): string | null => {
  const [logoRenderUrl, setLogoRenderUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const tintAndSet = (sourceUrl: string, fallbackUrl: string | null) => {
      const img = new Image();
      // No crossOrigin: source is always same-origin (/excerptus-logo.png or a blob: URL).
      // Setting it would force a CORS check that the static server doesn't satisfy,
      // making img.onerror fire and the corner mark disappear from the preview.
      img.onload = () => {
        if (cancelled) return;
        const scale = 4;
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, (img.naturalWidth || img.width || 1) * scale);
        canvas.height = Math.max(1, (img.naturalHeight || img.height || 1) * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          setLogoRenderUrl(fallbackUrl ?? sourceUrl);
          return;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "source-in";
        ctx.fillStyle = textColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        try {
          setLogoRenderUrl(canvas.toDataURL("image/png"));
        } catch {
          setLogoRenderUrl(fallbackUrl ?? sourceUrl);
        }
      };
      img.onerror = () => {
        if (!cancelled) setLogoRenderUrl(fallbackUrl);
      };
      img.src = sourceUrl;
    };

    (async () => {
      if (logoPath) {
        const { data, error } = await supabase.storage.from("logos").download(logoPath);
        if (error || !data) {
          if (!cancelled) setLogoRenderUrl(null);
          return;
        }
        objectUrl = URL.createObjectURL(data);
        // Custom uploads keep the tint pipeline so colors adapt to the background.
        tintAndSet(objectUrl, objectUrl);
      } else {
        // Default Excerptus mark — same auto-tint pipeline as uploads so the
        // mark stays legible (black on light backgrounds, near-white on dark)
        // at the shared logoOpacity. Fallback to the raw asset if tinting fails.
        tintAndSet("/excerptus-logo.png", "/excerptus-logo.png");
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [logoPath, textColor]);

  return logoRenderUrl;
};
